import React from 'react'
import { When } from 'react-if'
import { Button, LinkButton } from '@/atoms/Button'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { HeadingLG, ParagraphLG } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { Avatar } from '@/molecules/Avatar'
import { Modal } from '@/molecules/Modal'
import { CatalogTitle } from '@/services/ContentCatalog'
import { SharedContent } from '@/services/ContentShareService'
import { useUser } from '@/services/UserService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useTranslate } from '@/utils/translate/translate-client'
import { UserPublicProfile } from '@/utils/users/usersServer'

interface Props {
  showModal: boolean
  handleModalClose: () => void
  moviePosterPath?: string | null
  catalogTitle?: CatalogTitle
  sharedContent?: SharedContent
  sharerPublicProfile?: UserPublicProfile
  contentClaimed?: boolean
  hasUser?: boolean
  loginUrl: string
  sharerLocation?: string
  projectSlug?: string
  fallbackTitle: string
}

export const GuildContentRecipientModal: React.FC<Props> = ({
  showModal,
  handleModalClose,
  moviePosterPath,
  catalogTitle,
  sharedContent,
  sharerPublicProfile,
  contentClaimed = false,
  hasUser = false,
  loginUrl,
  sharerLocation,
  projectSlug,
  fallbackTitle,
}) => {
  const { t } = useTranslate('watch')
  const { user } = useUser()
  const sharerName = sharerPublicProfile?.firstName ?? t('anAngelGuildMember', 'a Guild Member')

  const handleStartWatching = () => {
    handleModalClose()
  }

  return (
    <Modal
      isOpen={showModal}
      onClose={handleModalClose}
      panelClasses="md:min-w-[88vw] lg:min-w-min !p-4 !pb-8 !max-w-[834px]"
    >
      <div className="flex flex-col">
        <div className="flex min-h-8 items-center justify-end">
          <When condition={!contentClaimed && hasUser}>
            <div onClick={handleModalClose} className="cursor-pointer">
              <CloseIcon color="black" size={32} className="cursor-pointer" />
            </div>
          </When>
        </div>
        <div className="px-6">
          <div className="flex flex-col">
            <HeadingLG weight="bold">
              {contentClaimed
                ? t('thisInviteLinkHasExpired', 'This invite link has expired or already been watched.')
                : sharedContent?.recipient_name?.length || user?.profile?.firstName
                ? t('helloRecipient', 'Hello {{ recipient }}!', {
                    recipient: sharedContent?.recipient_name?.length
                      ? sharedContent?.recipient_name
                      : user?.profile?.firstName,
                  })
                : t('helloUser', 'Hello!')}
            </HeadingLG>
            <div className="flex w-full flex-row gap-8 rounded-2xl py-10">
              <div>
                {moviePosterPath && (
                  <div className="block overflow-hidden rounded-lg">
                    <img
                      src={getCloudinaryImageUrl({ path: moviePosterPath, width: 150, height: 225 })}
                      className="min-h-[225px] min-w-[150px]"
                      alt={t('moviePosterAlt', '{{name}} Movie Poster', { name: catalogTitle?.title })}
                      height={225}
                      width={150}
                    />
                  </div>
                )}
              </div>
              <div className="flex w-full flex-col gap-4">
                <ParagraphLG weight={contentClaimed ? 'bold' : 'medium'}>
                  {contentClaimed ? `${t('thankYouForWatching', 'Thank you for watching')}!` : ''}
                </ParagraphLG>
                <ParagraphLG weight="medium">
                  {contentClaimed
                    ? t(
                        'signUpWatchMoreLikeThis',
                        'Sign up for the Guild to watch {{ projectName }} and many more light amplifying shows and movies!',
                        { projectName: catalogTitle?.title },
                      )
                    : sharedContent?.sender_note?.length
                    ? sharedContent.sender_note
                    : fallbackTitle}
                </ParagraphLG>
                <When condition={!contentClaimed}>
                  <div className="flex flex-row items-center gap-4">
                    <Avatar src={sharerPublicProfile?.image} size={28} />
                    <ParagraphLG weight="medium" color="core-gray-700">
                      {sharerPublicProfile?.firstName} {sharerPublicProfile?.lastName} {sharerLocation}
                    </ParagraphLG>
                  </div>
                  <When condition={hasUser}>
                    <Button
                      variant="core-gray-900"
                      className="mt-4 max-h-[56px] w-fit rounded-2xl px-7 py-[17px]"
                      onClick={handleStartWatching}
                    >
                      <PlayIcon color="white" size={24} className="mr-1.5" />
                      {t('watchFreeThanksTo', 'Watch Free Thanks to {{ sharerName }}', {
                        sharerName,
                      })}
                    </Button>
                  </When>
                  <When condition={!hasUser}>
                    <LinkButton
                      variant="core-gray-900"
                      className="mt-4 max-h-[56px] w-fit rounded-2xl px-7 py-[17px]"
                      href={loginUrl}
                    >
                      {t('signUpWatchFree', 'Sign Up to Watch Free')}
                    </LinkButton>
                  </When>
                </When>
                <When condition={contentClaimed}>
                  <div className="flex flex-col">
                    <div className="flex flex-row gap-4">
                      <LinkButton
                        href={`${paths.guild.join}/${projectSlug}`}
                        variant="core-gray-900"
                        className="mt-4 max-h-[56px] w-fit gap-1.5 rounded-2xl px-7 py-[17px]"
                        onClick={handleStartWatching}
                      >
                        <GuildIconMono color="white" size={24} />
                        {t('learnMore', 'Learn More')}
                      </LinkButton>
                      <LinkButton
                        href={paths.watch.index}
                        variant="core-gray-900"
                        className="mt-4 max-h-[56px] w-fit gap-1.5 rounded-2xl px-7 py-[17px]"
                        onClick={handleStartWatching}
                      >
                        {t('browseAngelOriginals', 'Browse Angel Originals')}
                      </LinkButton>
                    </div>
                    <When condition={!hasUser}>
                      <ParagraphLG weight="medium" className="mt-8">
                        <a
                          className="mt-4 max-h-[56px] w-fit min-w-[178px] cursor-pointer rounded-2xl py-[17px] underline"
                          href={loginUrl}
                        >
                          {t('alreadyClaimedLogin', 'Already claimed this link? Log in to watch!')}
                        </a>
                      </ParagraphLG>
                    </When>
                  </div>
                </When>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}
